// realtimeActions.js
'use strict';
import {
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import { AGORA_APP_ID } from '../RemoteConfigKeys.js';

import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getOperationData from '../selector/getOperationData.js';
import { getRealtimeChannels } from '../selector/getRealtimeComputedData.js';
import getNetworkingData from '../selector/getNetworkingData.js';

import handleRealtimeEvent from '../action/handleRealtimeEvent.js';
import mergeMeData from '../action/mergeMeData.js';

import {
  init as initAgoraRtm,
  login as loginAgoraRtm,
  setEventListeners as setAgoraRtmEventListeners,
  manageMessageChannel as manageAgoraRtmMessageChannel,
  manageStreamChannel as manageAgoraRtmStreamChannel,
  renewToken as renewAgoraRtmToken,
} from '../resource/agoraRtm.js';
import {
  RealtimeProvider,
  AgoraRealtimeChannelType,
  RealtimeServiceConnectionStatus,
} from '../resource/realtimeConstants.js';
import { realtime as realtimeDebug } from '../resource/debug.js';
import fetch from '../resource/customFetch.js';
import { API_V2 } from '../resource/resourceUrlTypeConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';

const realtimeLog = realtimeDebug.extend('log:realtimeActions');
const realtimeError = realtimeDebug.extend('error:realtimeActions');

const createKey = () => {
  return Math.random().toString(36).substr(2, 8);
};

const PendingItemStatus = {
  DEFAULT: 'default',
  PROCESSING: 'processing',
  ABORTED: 'aborted',
};

let pendingSubscribeMap = new Map();

let realtimeService;

let serviceInitPromiseResolve;
let serviceInitPromiseReject;
let serviceInitPromise = new Promise((resolve, reject) => {
  serviceInitPromiseResolve = resolve;
  serviceInitPromiseReject = reject;
});

/**
 * Initialize realtime service.
 * @kind action
 * @param {object} payload - action param.
 * @param {RealtimeProvider} [payload.provider = RealtimeProvider.AGORA] - realtime service provider.
 * @return {Promise} Action promise.
 */
const init =
  ({ provider = RealtimeProvider.AGORA } = {}) =>
  async dispatch => {
    realtimeLog('start init realtime service', { provider });
    try {
      if (!realtimeService) {
        switch (provider) {
          case RealtimeProvider.AGORA: {
            realtimeService = provider;

            await dispatch(initAgora());

            serviceInitPromiseResolve();
            break;
          }
          default: {
            throw new Error(`unsupported realtime provider: ${provider}`);
          }
        }
      } else {
        await serviceInitPromise;
      }
      realtimeLog('init realtime service done', { provider, realtimeService });
    } catch (error) {
      realtimeError('encounter error on init realtime service', {
        error,
        provider,
      });

      serviceInitPromiseReject(error);

      // TODO: if we have re-init realtime buttton, we can reset the realtime service here.
      // realtimeService = undefined;
      // serviceInitPromise = new Promise((resolve, reject) => {
      //   serviceInitPromiseResolve = resolve;
      //   serviceInitPromiseReject = reject;
      // });

      throw error;
    }
  };

/**
 * Subscribe realtime features.
 * @kind action
 * @param {object} payload - action param.
 * @param {Array<{
 *   feature: string,
 *   objectId: string,
 * }>} payload.features - realtime features.
 * @return {Promise} Action promise.
 */
const subscribeFeatures =
  ({ features = [] } = {}) =>
  async (dispatch, getState) => {
    const requestId = createKey();
    const log = realtimeLog.extend(`subscribeFeatures:${requestId}`);

    log('start subscribe features', { features });

    if (features.length === 0) return dispatch({ type: '' });

    // Collect the features to subscribe before the realtime service is initialized.
    // Use `Map` to avoid duplicate items.
    features.forEach(item => {
      const key = `${item.feature}:${item.objectId}`;

      if (pendingSubscribeMap.has(key)) {
        const { feature, objectId, status } = pendingSubscribeMap.get(key);

        if (PendingItemStatus.ABORTED === status) {
          pendingSubscribeMap.set(key, {
            feature,
            objectId,
            status: PendingItemStatus.DEFAULT,
          });
        }
      } else {
        pendingSubscribeMap.set(key, {
          feature: item.feature,
          objectId: item.objectId,
          status: PendingItemStatus.DEFAULT,
        });
      }
    });

    log('waiting for service init promise');
    await serviceInitPromise;
    log('finish service init', {
      pendingSubscribeItems: [...pendingSubscribeMap.values()],
    });

    const pendings = [];

    [...pendingSubscribeMap.entries()]
      .filter(([, { status }]) => PendingItemStatus.DEFAULT === status)
      .forEach(([key, item]) => {
        pendings.push({
          feature: item.feature,
          objectId: item.objectId,
        });

        pendingSubscribeMap.set(key, {
          ...item,
          status: PendingItemStatus.PROCESSING,
        });
      });

    log('generate pendings to fetch realtime channel authenticate', {
      pendings,
      pendingSubscribeMap: [...pendingSubscribeMap.values()],
    });

    if (pendings.length > 0) {
      const currentChannels = getChannelsByFeatures({
        features: pendings,
        getState,
      });

      log('current channels before fetch realtime channel authenticate', {
        pendings,
        currentChannels,
      });

      await dispatch(
        fetchRealtimeChannelAuthenticate({
          features: pendings,
        })
      );

      const channels = getChannelsByFeatures({
        features: pendings.filter(({ feature, objectId }) => {
          const key = `${feature}:${objectId}`;

          return (
            pendingSubscribeMap.has(key) &&
            PendingItemStatus.PROCESSING === pendingSubscribeMap.get(key).status
          );
        }),
        getState,
      });

      log('channels after fetch realtime channel authenticate', {
        pendings,
        currentChannels,
        channels,
      });

      pendings.forEach(({ feature, objectId }) => {
        const key = `${feature}:${objectId}`;
        pendingSubscribeMap.delete(key);
      });

      const { added: addedChannels, removed: removedChannels } =
        getChannelsDiff({
          current: currentChannels,
          next: channels,
          getState,
        });

      log('channels diff to join/leave channel', {
        addedChannels,
        removedChannels,
      });

      await Promise.all([
        ...removedChannels.map(item =>
          dispatch(manageChannel({ ...item, isJoinChannel: false }))
        ),
        ...addedChannels.map(item =>
          dispatch(manageChannel({ ...item, isJoinChannel: true }))
        ),
      ]);
    }

    log('finish subscribe features');
  };

/**
 * Unsubscribe realtime features.
 * @kind action
 * @param {object} payload - action param.
 * @param {Array<{
 *   feature: string,
 *   objectId: string,
 * }>} payload.features - realtime features.
 * @return {Promise} Action promise.
 */
const unsubscribeFeatures =
  ({ features = [] } = {}) =>
  async (dispatch, getState) => {
    const requestId = createKey();
    const log = realtimeLog.extend(`unsubscribeFeatures:${requestId}`);

    log('start unsubscribe features', {
      features,
      pendingSubscribeItems: [...pendingSubscribeMap.values()],
    });

    if (features.length === 0) return dispatch({ type: '' });

    features.forEach(({ feature, objectId }) => {
      const key = `${feature}:${objectId}`;
      if (pendingSubscribeMap.has(key)) {
        const { status } = pendingSubscribeMap.get(key);
        switch (status) {
          case PendingItemStatus.DEFAULT: {
            pendingSubscribeMap.delete(key);
            break;
          }
          case PendingItemStatus.PROCESSING: {
            pendingSubscribeMap.set(key, {
              feature,
              objectId,
              status: PendingItemStatus.ABORTED,
            });
            break;
          }
          case PendingItemStatus.ABORTED:
          default: {
            break;
          }
        }
      } else {
        pendingSubscribeMap.set(key, {
          feature,
          objectId,
          status: PendingItemStatus.ABORTED,
        });
      }
    });

    log('waiting for service init promise');
    await serviceInitPromise;
    log('finish service init', {
      pendingSubscribeItems: [...pendingSubscribeMap.values()],
    });

    const pendings = [...pendingSubscribeMap.values()]
      .filter(({ status }) => PendingItemStatus.ABORTED === status)
      .map(item => ({
        feature: item.feature,
        objectId: item.objectId,
      }));

    log('to unsubscribe features', {
      pendings,
    });

    const channels = getChannelsByFeatures({ features: pendings, getState });

    log('channels to leave', {
      pendings,
      channels,
    });

    // remove features and related channels
    dispatch({
      type: REMOVE_OPERATION_DATA,
      payload: {
        selectPaths: [
          ...pendings.map(({ feature, objectId }) => [
            'realtime',
            'feature',
            feature,
            objectId,
          ]),
          ...channels.map(({ channel }) => ['realtime', 'channel', channel]),
        ],
      },
    });

    await Promise.all(
      channels.map(item =>
        dispatch(
          manageChannel({
            ...item,
            isJoinChannel: false,
          })
        )
      )
    );

    log('finish unsubscribe features');
  };

/**
 * Initialize agora realtime service.
 * @kind action
 * @return {Promise} Action promise.
 */
const initAgora = () => async (dispatch, getState) => {
  const appId = getRemoteConfigData(getState(), AGORA_APP_ID);
  const sessionId = getMeData(getState(), 'sessionId');

  await initAgoraRtm({
    appId,
    sessionId,
  });

  const handlerLog = realtimeLog.extend('eventListener');
  const handlerErrorLog = realtimeError.extend('eventListener');

  await setAgoraRtmEventListeners({
    listeners: [
      {
        name: 'message',
        handler: async event => {
          try {
            handlerLog('handle message event', { event });

            const { event: realtimeEventName, data: realtimeEventData } =
              JSON.parse(event.message);
            const channelName = event.channelName;
            const objectId = getOperationData(getState(), [
              'realtime',
              'channel',
              channelName,
              'objectId',
            ]);

            return dispatch(
              handleRealtimeEvent({
                id: objectId,
                event: realtimeEventName,
                via: 'realtime',
                data: realtimeEventData,
              })
            );
          } catch (error) {
            handlerErrorLog('encounter error on handle message event', {
              event,
              error,
            });
          }
        },
      },
      {
        name: 'linkState',
        handler: event => {
          handlerLog('handle linkState event', { event });

          try {
            const status = event.currentState.toLowerCase();

            dispatch(
              updateConnectionStatus({
                status,
                reason: event.reason,
                timestamp: event.timestamp,
                channelNames: event.affectedChannels,
              })
            );
          } catch (error) {
            handlerErrorLog('encounter error on handle status event', {
              event,
              error,
            });
            dispatch(
              updateConnectionStatus({
                status: RealtimeServiceConnectionStatus.FAILED,
                reason: error.message,
                timestamp: Date.now(),
              })
            );
          }
        },
      },
      // {
      //   name: 'status',
      //   handler: event => {
      //     try {
      //       handlerLog('handle status event', { event });

      //       dispatch(
      //         updateConnectionStatus({
      //           status: event.state.toLowerCase(),
      //           reason: event.reason,
      //           timestamp: event.timestamp,
      //         })
      //       );
      //     } catch (error) {
      //       handlerErrorLog('encounter error on handle status event', {
      //         event,
      //         error,
      //       });

      //       dispatch(
      //         updateConnectionStatus({
      //           status: RealtimeServiceConnectionStatus.FAILED,
      //           reason: error.message,
      //           timestamp: Date.now(),
      //         })
      //       );
      //     }
      //   },
      // },
      {
        name: 'tokenPrivilegeWillExpire',
        handler: async channelName => {
          handlerLog('handle tokenPrivilegeWillExpire event', {
            channelName,
          });

          if (!channelName) {
            await dispatch(
              fetchRealtimeServiceToken({
                provider: RealtimeProvider.AGORA,
                sessionId,
              })
            );
            renewAgoraRtmToken({
              token: getMeData(getState(), 'realtimeServiceToken')?.token,
            });
          }
        },
      },
    ],
  });

  await dispatch(
    fetchRealtimeServiceToken({
      provider: RealtimeProvider.AGORA,
      sessionId,
    })
  );

  await loginAgoraRtm({
    token: getMeData(getState(), 'realtimeServiceToken')?.token,
  });
};

/**
 * Get realtime service token.
 * @kind action
 * @param {object} payload - action param.
 * @param {RealtimeProvider} payload.provider - realtime service provider.
 * @param {string} payload.sessionId - session id.
 * @return {Promise} Action promise.
 * @see https://api-v2.swag.live/docs#/realtime/tokens_realtime_tokens__provider__post
 */
const fetchRealtimeServiceToken =
  ({ provider, sessionId } = {}) =>
  async (dispatch, getState) => {
    const networkingSelectPath = ['submit', 'realtimeServiceToken', provider];
    const token = getMeData(getState(), 'token');
    const isFetching = getNetworkingData(
      getState(),
      networkingSelectPath,
      'isFetching'
    );

    if (!sessionId || isFetching) return dispatch({ type: '' });

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid: sessionId,
      }),
    };

    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const fetchUrl = getResourceUrl({
      endpoint: `/realtime/tokens/${provider}`,
      resourceType: API_V2,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }

      const { token, exp } = await response.json();

      dispatch(
        mergeMeData({
          field: 'realtimeServiceToken',
          value: {
            provider,
            token,
            exp,
          },
        })
      );
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

/**
 * Get realtime features related channels and tokens.
 * @kind action
 * @param {object} payload - action param.
 * @param {array} payload.features - realtime features.
 * @param {RealtimeFeatureName} payload.features[].feature - realtime feature name.
 * @param {string} payload.features[].objectId - the target object id.
 * @param {string} payload.features[].uid - the id that server side generate channel token.
 * @return {Promise} Action promise.
 * @see https://api-v2.swag.live/docs#/realtime/authenticate_realtime_authenticate_post
 */
const fetchRealtimeChannelAuthenticate =
  ({ features = [] } = {}) =>
  async (dispatch, getState) => {
    const networkingSelectPath = ['submit', 'realtimeChannelAuthenticate'];
    const token = getMeData(getState(), 'token');
    const sessionId = getMeData(getState(), 'sessionId');

    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(features.map(item => ({ ...item, uid: sessionId }))),
    };

    if (token) {
      fetchOptions.headers.Authorization = `Bearer ${token}`;
    }

    const fetchUrl = getResourceUrl({
      endpoint: '/realtime/authenticate',
      resourceType: API_V2,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }

      // [
      //   {
      //     feature: 'livestream',
      //     objectId: '5d6350f727e7bf81ba90c31a',
      //     provider: 'agora',
      //     channel: 'livestream#session@5d6350f727e7bf81ba90c31a',
      //     token: '',
      //     metadata: {
      //       type: 'session',
      //       channelType: 'message',
      //       topic: null,
      //       exp: 1764753691,
      //     },
      //   },
      // ];
      const payload = ((await response.json()) || []).filter(
        ({ feature, objectId }) => {
          const key = `${feature}:${objectId}`;

          return (
            pendingSubscribeMap.has(key) &&
            PendingItemStatus.PROCESSING === pendingSubscribeMap.get(key).status
          );
        }
      );

      const currentChannels = getChannelsByFeatures({ features, getState });
      const channels = payload;
      const { removed } = getChannelsDiff({
        current: currentChannels,
        next: channels,
        getState,
      });

      // remove removed channels
      dispatch({
        type: REMOVE_OPERATION_DATA,
        payload: {
          selectPaths: [
            ...removed
              .map(({ feature, objectId, channel }) => {
                return [
                  ['realtime', 'channel', channel],
                  ['realtime', 'feature', feature, objectId, channel],
                ];
              })
              .flat(),
          ],
        },
      });
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['realtime', 'feature'],
          data: payload.reduce((accumulated, item) => {
            const { feature, objectId, channel } = item;
            if (!accumulated[feature]) {
              accumulated[feature] = {};
            }
            if (!accumulated[feature][objectId]) {
              accumulated[feature][objectId] = {};
            }
            accumulated[feature][objectId][channel] = channel;
            return accumulated;
          }, {}),
        },
      });
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['realtime', 'channel'],
          data: objectifyArrayById({
            array: payload,
            keyAsId: 'channel',
          }),
        },
      });
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

/**
 * Get realtime channel objects by features.
 * @param {object} payload - payload object.
 * @param {array} payload.features - realtime features.
 * @param {function} payload.getState - get state function.
 * @return {Array<{
 *   feature: string,
 *   objectId: string,
 *   provider: string,
 *   channel: string,
 *   token: string,
 *   metadata: {
 *     type: string,
 *     channelType: string,
 *     topic: string,
 *     exp: number,
 *   },
 * }>} channels
 */
const getChannelsByFeatures = ({ features = [], getState }) => {
  const channels = features
    .map(({ feature, objectId }) =>
      getRealtimeChannels(getState(), feature, objectId)
    )
    .flat();

  return channels;
};

/**
 * Get diff of realtime channel objects by features.
 * @param {object} payload - payload object.
 * @param {array} payload.current - current channels.
 * @param {array} payload.next - next channels.
 * @return {object} diff object.
 * @return {array} diff.added - added channels.
 * @return {array} diff.removed - removed channels.
 */
const getChannelsDiff = ({ current = [], next = [], getState }) => {
  const added = next.filter(item => {
    const isExist = current.some(
      currentItem => currentItem.channel === item.channel
    );
    const isConnected =
      RealtimeServiceConnectionStatus.CONNECTED ===
      getOperationData(
        getState(),
        ['realtime', 'channel', item.channel, 'status'],
        'status'
      );
    return !isExist || !isConnected;
  });
  const removed = current.filter(
    item => !next.some(nextItem => nextItem.channel === item.channel)
  );

  return {
    added,
    removed,
  };
};

const updateConnectionStatus =
  ({ status, reason, timestamp = Date.now(), channelNames = [] }) =>
  async dispatch => {
    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['realtime', 'connection'],
        data: {
          status,
          reason,
          timestamp,
        },
      },
    });

    switch (status) {
      case RealtimeServiceConnectionStatus.CONNECTED: {
        const data = channelNames.reduce((accumulated, channelName) => {
          accumulated[channelName] = {
            status: {
              status: RealtimeServiceConnectionStatus.CONNECTED,
              reason: '',
              timestamp,
            },
          };
          return accumulated;
        }, {});

        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['realtime', 'channel'],
            data,
          },
        });
        break;
      }
      default: {
        const data = channelNames.reduce((accumulated, channelName) => {
          accumulated[channelName] = {
            status: {
              status: RealtimeServiceConnectionStatus.DISCONNECTED,
              reason: 'agora rtm connection lost',
              timestamp,
            },
          };
          return accumulated;
        }, {});

        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['realtime', 'channel'],
            data,
          },
        });

        break;
      }
    }
  };

/**
 * Manage realtime channel.
 * @kind action
 * @param {object} payload - action param.
 * @param {string} payload.channel - realtime channel name.
 * @param {object} payload.metadata - realtime channel metadata.
 * @param {string} payload.token - realtime channel token.
 * @param {boolean} payload.isJoinChannel - whether to join realtime channel.
 * @return {Promise} Action promise.
 */
const manageChannel =
  ({ channel, metadata, token, isJoinChannel = true } = {}) =>
  async dispatch => {
    const channelStatusSelectPath = ['realtime', 'channel', channel, 'status'];

    if (isJoinChannel) {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: channelStatusSelectPath,
          data: {
            status: RealtimeServiceConnectionStatus.CONNECTING,
            reason: '',
            timestamp: Date.now(),
          },
        },
      });
    }

    switch (metadata.channelType) {
      case AgoraRealtimeChannelType.MESSAGE:
        await manageAgoraRtmMessageChannel({
          channelName: channel,
          isJoinChannel,
        });
        break;
      case AgoraRealtimeChannelType.STREAM:
        await manageAgoraRtmStreamChannel({
          channelName: channel,
          token,
          isJoinChannel,
        });
        break;
      default:
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: channelStatusSelectPath,
            data: {
              status: RealtimeServiceConnectionStatus.FAILED,
              reason: 'channel type not supported',
              timestamp: Date.now(),
            },
          },
        });
        throw new Error('channel type not supported');
    }

    if (isJoinChannel) {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: channelStatusSelectPath,
          data: {
            status: RealtimeServiceConnectionStatus.CONNECTED,
            reason: '',
            timestamp: Date.now(),
          },
        },
      });
    }
  };

export { init, subscribeFeatures, unsubscribeFeatures };
